import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

import { technicalDepartmentId } from '../../../utils/constants/departmentDetails';

/**
 * TEMPORARY. A note at the bottom of each detail popup saying exactly where the
 * number came from: which table, which column, which way the joins run and what
 * is finally added up.
 *
 * WHY IT EXISTS
 *
 * The first live month is being compared by hand against the sheet the department
 * keeps. When a figure looks wrong the first question is always the same, WHERE
 * DID THIS COME FROM, and answering it meant opening the code.
 *
 * WHY IT IS A FLOW AND NOT A QUERY
 *
 * A finished query is only useful to somebody who is going to paste it in. The
 * flow can be READ OUT to the database admin, who then writes whatever query
 * suits them. It is also the thing that has to be agreed. Two people can argue
 * about a query for an hour and still not have said which column the money comes
 * from.
 *
 * WHO SEES IT
 *
 * Only the Technical Department, department 5. It is written for whoever
 * maintains the code, and on an OGA's screen a line like "this table holds under
 * half the assessments" reads as an accusation rather than as a note about the
 * database. Everybody else gets nothing at all, not a disabled or empty strip.
 *
 * The department comes from the logged in profile in localStorage, the same
 * `cred.profile.departmentId` the rest of the app reads. This is a COURTESY, not
 * security: it hides a block of text, and there is nothing behind it to protect.
 *
 * REMOVING IT
 *
 * Delete this file and the four <DeveloperNote /> tags that use it. Nothing else
 * depends on it. It is collapsed by default, so it costs one line of space until
 * somebody opens it.
 */

/**
 * The department of whoever is logged in, or null when there is nothing readable.
 *
 * Wrapped, because a missing or half written `cred` throws on parse, and a note
 * for developers must never be the thing that takes a page down.
 */
const myDepartmentId = () => {
  try {
    return Number(JSON.parse(localStorage.getItem('cred'))?.profile?.departmentId);
  } catch {
    return null;
  }
};

const DeveloperNote = ({ title, steps = [], lines = [] }) => {
  // not the Technical Department means the note is not rendered at all
  if (myDepartmentId() !== technicalDepartmentId) return null;

  return (
    <Accordion
      disableGutters
      sx={{
        mt: 2,
        boxShadow: 'none',
        border: '1px dashed',
        borderColor: 'divider',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon icon="mdi:table-arrow-right" />
          <Typography variant="body2" fontWeight={600}>
            Developer note · where this number comes from
          </Typography>
          <Chip size="small" variant="outlined" color="warning" label="Temporary" />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          <Alert severity="info" icon={<Icon icon="mdi:information-outline" />}>
            Here only while the first live month is being checked against the sheet.
            Read this out to the database admin and they can rebuild the same figure
            from the live tables. It will be removed once the numbers are agreed.
          </Alert>

          <Typography variant="subtitle2">{title}</Typography>

          {/* the flow, one step per line, numbered so it can be read out in order
              over a call without anybody losing their place */}
          <Box>
            {steps.map((step, index) => (
              <Stack
                key={step.what}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                sx={{
                  py: 1,
                  borderBottom: index === steps.length - 1 ? 'none' : '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    minWidth: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    fontSize: '0.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 0.2,
                  }}
                >
                  {index + 1}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {step.what}
                  </Typography>
                  {/* the table and column names, kept in monospace so a full stop
                      inside a name cannot be mistaken for the end of a sentence */}
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'primary.dark' }}
                  >
                    {step.where}
                  </Typography>
                  {step.why && (
                    <Typography variant="caption" color="text.secondary">
                      {step.why}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Box>

          {lines.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Worth knowing
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {lines.map((line) => (
                  <Typography component="li" variant="body2" key={line} sx={{ mb: 0.5 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default DeveloperNote;
